import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, HeartHandshake, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/format";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { OfferingCard } from "@/components/marketing/OfferingCard";
import { CoachCard } from "@/components/marketing/CoachCard";

const HERO_IMG = "https://images.pexels.com/photos/20516335/pexels-photo-20516335.jpeg";
const STEP_ICONS = [Compass, HeartHandshake, Sparkles];

export default function Home() {
  const t = useT();
  usePageMeta(t("home.meta_title"), t("home.meta_desc"));
  const [offerings, setOfferings] = useState(null);
  const [coaches, setCoaches] = useState(null);

  useEffect(() => {
    api.get("/public/offerings", { params: { page: 0, page_size: 6 } })
      .then((r) => setOfferings(r.data.items || []))
      .catch(() => setOfferings([]));
    api.get("/public/coaches")
      .then((r) => setCoaches((r.data || []).slice(0, 3)))
      .catch(() => setCoaches([]));
  }, []);

  const steps = STEP_ICONS.map((icon, i) => ({
    icon,
    title: t(`home.step${i + 1}_title`),
    text: t(`home.step${i + 1}_text`),
  }));

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2A] via-[#2C2C2A]/70 to-[#2C2C2A]/30" />
        <div className="container-page relative flex min-h-[78vh] flex-col justify-center py-24 text-oat">
          <span className="animate-fade-up text-sm font-medium uppercase tracking-[0.2em] text-oat/80">
            {t("home.hero_eyebrow")}
          </span>
          <h1 className="mt-4 max-w-3xl animate-fade-up font-display text-4xl font-semibold leading-[1.05] text-oat sm:text-5xl lg:text-6xl" style={{ animationDelay: "60ms" }}>
            {t("home.hero_title")}
          </h1>
          <p className="mt-6 max-w-xl animate-fade-up text-base text-oat/85 sm:text-lg" style={{ animationDelay: "120ms" }}>
            {t("home.hero_sub")}
          </p>
          <div className="mt-9 flex animate-fade-up flex-wrap gap-3" style={{ animationDelay: "180ms" }}>
            <Button size="lg" className="rounded-full px-7 text-base" asChild data-testid="hero-marketplace-btn">
              <Link to="/marketplace">{t("home.hero_cta_marketplace")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
            <Button size="lg" variant="outline" className="rounded-full border-oat/40 bg-transparent px-7 text-base text-oat hover:bg-oat/10 hover:text-oat" asChild data-testid="hero-signup-btn">
              <Link to="/signup">{t("home.hero_cta_signup")}</Link>
            </Button>
          </div>
          <p className="mt-4 animate-fade-up text-sm text-oat/70" style={{ animationDelay: "220ms" }} data-testid="hero-free-hint">
            {t("home.hero_free_hint")}
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="container-page py-20">
        <div className="grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <div key={s.title} className="animate-fade-up rounded-2xl border border-border bg-card p-7" style={{ animationDelay: `${i * 70}ms` }}>
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-accent text-accent-foreground">
                <s.icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-xl text-foreground">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured offerings */}
      <section className="container-page pb-6">
        <div className="mb-7 flex items-end justify-between">
          <div>
            <h2 className="font-display text-3xl text-foreground">{t("home.offerings_title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("home.offerings_sub")}</p>
          </div>
          <Link to="/marketplace" className="hidden text-sm font-medium text-primary hover:underline sm:inline-flex">
            {t("home.see_all")} <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {offerings === null
            ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-80 rounded-2xl" />)
            : offerings.map((o) => <OfferingCard key={o.id} offering={o} />)}
        </div>
      </section>

      {/* Featured coaches */}
      <section className="container-page py-16">
        <div className="mb-7 flex items-end justify-between">
          <div>
            <h2 className="font-display text-3xl text-foreground">{t("home.coaches_title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("home.coaches_sub")}</p>
          </div>
          <Link to="/coaches" className="hidden text-sm font-medium text-primary hover:underline sm:inline-flex">
            {t("home.all_coaches")} <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {coaches === null
            ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-2xl" />)
            : coaches.map((c) => <CoachCard key={c.id} coach={c} />)}
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-primary">
        <div className="container-page flex flex-col items-center gap-5 py-16 text-center text-primary-foreground">
          <h2 className="max-w-2xl font-display text-3xl sm:text-4xl">{t("home.cta_title")}</h2>
          <p className="max-w-lg text-primary-foreground/85">{t("home.cta_sub")}</p>
          <Button size="lg" variant="secondary" className="rounded-full px-8 text-base" asChild data-testid="cta-signup-btn">
            <Link to="/signup">{t("home.cta_btn")}</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
